import React, { useState, useEffect } from "react";
import { FaUniversity, FaLink, FaCity, FaGlobe, FaUserGraduate, FaUsers, FaUserTie, FaUser, FaCalendarAlt, FaCheckCircle, FaFileAlt, FaStar, FaMagic, FaEnvelope, FaLock } from "react-icons/fa";
import TipTapEditor from "../common/TipTapEditor";
import { useMutations } from "../../hooks/useMutations";
import { OpportunityService } from "../../services/OpportunityService";
import { useSnackBar } from "../../hooks/useSnackBar";
import { canManageOpportunities } from "../../utils/permissions";

const SaasUploadOpportunitySection = () => {
  const [currentUser, setCurrentUser] = useState(null);
  const [focusedField, setFocusedField] = useState(null);
  const [formData, setFormData] = useState({
    category: "standard",
    title: "",
    department: "",
    university: "",
    university_url: "",
    city: "",
    country: "",
    closing_date: "",
    open_to: "",
    supervisor: "",
    supervisor_url: "",
    type: "PhD",
    fully_funded: false,
    description: "",
    status: "pending"
  });

  // Add the mutation hook and snackbar
  const { mutateAsync: saveOpportunity, isLoading: saving } = useMutations(
    OpportunityService.createSaasOpportunity
  );
  
  const enqMsg = useSnackBar();

  useEffect(() => {
    
    // Get current user from localStorage
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        const userData = JSON.parse(userStr);
        setCurrentUser(userData);
      } catch (error) {
        console.error('Error parsing user data:', error);
      }
    }
    
    // Fetch current customer info
    OpportunityService.getCurrentCustomer().then((res) => {
      if (res && !res.error) {
        setFormData((prev) => ({
          ...prev,
          university: res.university || '',
          university_url: res.university_url || '',
          city: res.city || '',
          country: res.country || '',
        }));
      }
    }).catch((error) => {
      console.error('SaasUploadOpportunitySection: Failed to fetch customer info:', error);
    });
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  const handleDescriptionChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      description: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const closingDateEpoch = formData?.closing_date
        ? Math.floor(
            Date.UTC(
              ...formData.closing_date
                .split("-")
                .map((v, i) => (i === 1 ? Number(v) - 1 : Number(v)))
            ) / 1000
          )
        : null;

      const closingDateString = closingDateEpoch ? closingDateEpoch.toString() : null;

      // Call the actual API using the mutation
      const res = await saveOpportunity({
        ...formData,
        closing_date: closingDateString,
        fully_funded: formData?.fully_funded === true, // Ensure boolean conversion
      });

      if (res?.error) {
        throw new Error(res?.error);
      } else {
        enqMsg(res?.message || "Opportunity created successfully! 🎉", "success");
        
        // Reset form after successful submission
        setTimeout(() => {
          setFormData({
            category: "standard",
            title: "",
            department: "",
            university: "",
            university_url: "",
            city: "",
            country: "",
            closing_date: "",
            open_to: "",
            supervisor: "",
            supervisor_url: "",
            type: "PhD",
            fully_funded: false,
            description: "",
            status: "pending"
          });
        }, 1000);
      }
      
    } catch (error) {
      enqMsg(error?.message || "Failed to create opportunity. Please try again.", "error");
    }
  };

  // Rearranged fieldConfigs for better UX: university info at top, then title/department, then others
  const fieldConfigs = [
    { name: "university", icon: FaUniversity, placeholder: "e.g., Stanford University" },
    { name: "university_url", icon: FaLink, placeholder: "https://university.edu" },
    { name: "city", icon: FaCity, placeholder: "e.g., Palo Alto" },
    { name: "country", icon: FaGlobe, placeholder: "e.g., United States" },
    { name: "title", icon: FaUserGraduate, placeholder: "e.g., Machine Learning in Healthcare PhD Position" },
    { name: "department", icon: FaUsers, placeholder: "e.g., Computer Science Department" },
    { name: "supervisor", icon: FaUserTie, placeholder: "e.g., Prof. Jane Smith" },
    { name: "supervisor_url", icon: FaEnvelope, placeholder: "e.g., prof@university.edu or https://linkedin.com/in/prof" },
    { name: "open_to", icon: FaUsers, placeholder: "e.g., International students, EU citizens" },
    { name: "closing_date", icon: FaCalendarAlt, placeholder: "" },
  ];

  const requiredFields = ["title", "university", "department", "city", "country", "closing_date"];

  // Check if user has permission to manage opportunities
  if (!canManageOpportunities(currentUser)) {
    return (
      <div className="min-h-screen bg-[#FFEEED] py-8 px-4 sm:px-6 lg:px-8">
        <div className="relative max-w-4xl mx-auto">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-400 rounded-full mb-6 shadow-lg">
              <FaLock className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-4xl font-bold text-gray-600 mb-4">
              Access Restricted
            </h1>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              You don't have permission to upload opportunities. Please contact your administrator for access.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFEEED] py-8 px-4 sm:px-6 lg:px-8">
      {/* Floating background elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-[#FFD6D4] rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-pulse"></div>
        <div className="absolute top-3/4 right-1/4 w-96 h-96 bg-[#FFEEED] rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-3/4 w-80 h-80 bg-[#FFD6D4] rounded-full mix-blend-multiply filter blur-xl opacity-25 animate-pulse delay-500"></div>
      </div>

      <div className="relative max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-[#DB4F40] rounded-full mb-6 shadow-lg">
            <FaUserGraduate className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-4xl font-bold text-[#DB4F40] mb-4">
            Create PhD Opportunity
          </h1>
          <p className="text-[#1B222A] text-lg max-w-2xl mx-auto opacity-80">
            Share an amazing research opportunity with the academic community
          </p>
        </div>

        {/* Main Form Card */}
        <div className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-2xl border border-[#FFD6D4] overflow-hidden">
          <form onSubmit={handleSubmit} className="p-8 sm:p-12">
            <div className="space-y-8">
              {/* Form Fields Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {fieldConfigs.map((field) => {
                  const Icon = field.icon;
                  const isRequired = requiredFields.includes(field.name);
                  const isFocused = focusedField === field.name;
                  if (field.name === "closing_date") {
                    // Render Deadline and Funding Status together
                    return (
                      <div key={field.name} className="md:col-span-2 flex flex-col md:flex-row md:items-end gap-4">
                        <div className="flex-1">
                          <label className="flex items-center text-sm font-semibold text-[#1B222A] mb-3">
                            <Icon className="w-4 h-4 mr-2 text-[#DB4F40]" />
                            Deadline
                            {isRequired && <span className="text-red-500 ml-1">*</span>}
                          </label>
                          <div className={`relative group transition-all duration-300 ${isFocused ? 'transform scale-105' : ''}`}> 
                            <input
                              type="date"
                              name={field.name}
                              value={formData[field.name]}
                              onChange={handleInputChange}
                              onFocus={() => setFocusedField(field.name)}
                              onBlur={() => setFocusedField(null)}
                              className="w-full px-4 py-3 bg-white/60 border-2 border-[#FFD6D4] rounded-xl focus:border-[#DB4F40] focus:bg-white transition-all duration-300 placeholder-[#DB4F40]/60 shadow-sm hover:shadow-md"
                              required={isRequired}
                              placeholder={field.placeholder}
                            />
                            <div className="absolute inset-0 rounded-xl bg-[#FFD6D4] opacity-0 group-hover:opacity-10 transition-opacity duration-300 pointer-events-none"></div>
                          </div>
                        </div>
                        {/* Funding Status Checkbox next to Deadline */}
                        <div className="flex items-center space-x-3 md:mb-0 md:ml-6 mb-0 h-full" style={{ alignSelf: 'center' }}>
                          <input
                            type="checkbox"
                            name="fully_funded"
                            checked={formData.fully_funded}
                            onChange={handleInputChange}
                            id="fully_funded"
                            className="h-5 w-5 text-[#DB4F40] border-[#FFD6D4] rounded focus:ring-[#DB4F40]"
                          />
                          <label htmlFor="fully_funded" className="text-[#DB4F40] font-medium select-none">
                            Fully Funded
                          </label>
                        </div>
                      </div>
                    );
                  }
                  // All other fields
                  return (
                    <div key={field.name} className={field.name === "title" ? "md:col-span-2" : ""}>
                      <label className="flex items-center text-sm font-semibold text-[#1B222A] mb-3">
                        <Icon className="w-4 h-4 mr-2 text-[#DB4F40]" />
                        {field.name === "supervisor_url"
                          ? "Supervisor email/LinkedIn URL"
                          : field.name.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())}
                        {isRequired && <span className="text-red-500 ml-1">*</span>}
                      </label>
                      <div className={`relative group transition-all duration-300 ${isFocused ? 'transform scale-105' : ''}`}> 
                        <input
                          type={field.name === "supervisor_url" ? "text" : (field.name.includes("url") ? "url" : "text")}
                          name={field.name}
                          value={formData[field.name]}
                          onChange={handleInputChange}
                          onFocus={() => setFocusedField(field.name)}
                          onBlur={() => setFocusedField(null)}
                          className="w-full px-4 py-3 bg-white/60 border-2 border-[#FFD6D4] rounded-xl focus:border-[#DB4F40] focus:bg-white transition-all duration-300 placeholder-[#DB4F40]/60 shadow-sm hover:shadow-md"
                          required={isRequired}
                          placeholder={field.placeholder}
                        />
                        <div className="absolute inset-0 rounded-xl bg-[#FFD6D4] opacity-0 group-hover:opacity-10 transition-opacity duration-300 pointer-events-none"></div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Enhanced Description Section */}
              <div className="bg-[#FFD6D4] rounded-2xl p-6 border border-[#FFD6D4]">
                <label className="flex items-center text-sm font-semibold text-[#1B222A] mb-4">
                  <FaFileAlt className="w-4 h-4 mr-2 text-[#DB4F40]" />
                  Opportunity Description
                </label>
                
                <TipTapEditor
                  value={formData.description}
                  onChange={handleDescriptionChange}
                  placeholder="✨ Describe the research opportunity, requirements, benefits, and what makes this position special..."
                />
                
                <div className="mt-4 flex items-center text-xs text-[#1B222A] opacity-80">
                  <FaStar className="w-3 h-3 mr-1 text-[#DB4F40]" />
                  <span>Pro tip: Include research focus, required skills, application process, and what the candidate will learn</span>
                </div>
              </div>

              {/* Submit Button */}
              <div className="pt-6">
                <button
                  type="submit"
                  disabled={saving}
                  className="w-full bg-[#DB4F40] hover:bg-[#c13426] text-white px-8 py-4 rounded-2xl font-semibold text-lg shadow-xl hover:shadow-2xl focus:outline-none focus:ring-4 focus:ring-[#FFD6D4] transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                >
                  <div className="flex items-center justify-center">
                    {saving ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                        Creating Opportunity...
                      </>
                    ) : (
                      <>
                        <FaMagic className="h-5 w-5 mr-3 text-[#FFD6D4]" />
                        Publish Opportunity
                        <FaStar className="h-4 w-4 ml-2 text-[#FFD6D4]" />
                      </>
                    )}
                  </div>
                </button>
              </div>
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="text-center mt-8 text-[#1B222A] text-sm opacity-80">
          <p>Help build the future of academic research by sharing opportunities 🚀</p>
        </div>
      </div>
    </div>
  );
};

export default SaasUploadOpportunitySection;